import type {
  AllProductsQueryParams,
  ProductListItem,
} from '@/common/types/product.type';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import Client from '@/common/utils/client.utility';
import { createQueryStringUtility } from '@/common/utils/create-query-string.utility';
import serializeIdentifier from '@/common/utils/serialize-identifier.utility';

export interface ResponseItemsType<T> {
  data: {
    productCount: number;
    vendorName: string;
    items: T[];
  };
}

export interface ProductsResponse extends ProductListItem {}

export type ProductsType = (
  params: AllProductsQueryParams
) => Promise<ResponseItemsType<ProductsResponse>>;

export const getProducts: ProductsType = async ({
  vendorSlug,
  pageSize,
  pageIndex,
  sortDirection,
  sortCondition,
  categoryList,
  isAvailable,
  isDiscounted,
  searchByProductName,
}) => {
  const client = new Client();

  const slug = serializeIdentifier(vendorSlug);
  const queryString = createQueryStringUtility({
    searchByProductName,
    pageIndex,
    pageSize,
    sortDirection,
    sortCondition,
    isDiscounted,
    isAvailable,
    categoryList,
  });
  // eslint-disable-next-line sonarjs/no-nested-template-literals
  const endpoint = `/v1/Shopper/Storefronts${slug ? `/${slug}` : ''}/Product${queryString}`;
  return await client.get(endpoint);
};

export function useGetStoreProductList({
  enabled,
  params,
}: {
  enabled?: boolean;
  params: AllProductsQueryParams;
}) {
  return useQuery<ResponseItemsType<ProductsResponse>>({
    queryKey: ['products', params],
    queryFn: async () => await getProducts({ ...params }),
    placeholderData: keepPreviousData,
    enabled,
    staleTime: 600000,
  });
}
